import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { Plus, LifeBuoy, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { useAuth } from "@/contexts/AuthContext";
import { ticketsApi, ApiError, type Ticket, type TicketStatus } from "@/lib/api";
import { NoApiKeyBanner } from "../components/ApiKeyGate";

// Filter pills mirror the backend ticket_status enum exactly. The `value`
// field is what we put on the wire; `label` is what the user sees. "All"
// is sent as the literal "all" — the backend treats it as the no-filter
// sentinel.
const FILTERS: Array<{ label: string; value: "all" | TicketStatus }> = [
  { label: "All", value: "all" },
  { label: "Reported", value: "reported" },
  { label: "Queued", value: "queued" },
  { label: "In progress", value: "workinprogress" },
  { label: "Pending approval", value: "pending_approval" },
  { label: "Resolved", value: "resolved" },
  { label: "Not a bug", value: "notabug" },
  { label: "Closed", value: "closed" },
];

export function TenantTicketsList({ tenantCode }: { tenantCode: string }) {
  const { idToken } = useAuth();
  const [filter, setFilter] = useState<"all" | TicketStatus>("all");

  const query = useQuery<Ticket[], ApiError>({
    queryKey: ["tickets", tenantCode, filter, idToken],
    queryFn: async () => {
      if (!idToken) return [];
      return ticketsApi.list(tenantCode, idToken, filter === "all" ? undefined : filter);
    },
    enabled: !!idToken,
  });

  const tickets = query.data ?? [];

  const body = useMemo(() => {
    if (query.isLoading) {
      return (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      );
    }
    if (query.isError) {
      return (
        <EmptyOrError
          Icon={AlertCircle}
          title="Couldn't load tickets"
          body={query.error.message}
        />
      );
    }
    if (!tickets.length) {
      return (
        <EmptyOrError
          Icon={LifeBuoy}
          title="No tickets yet"
          body="Create a test ticket or wait for one to come in from your integrations."
        />
      );
    }
    return <TicketsTable tickets={tickets} />;
  }, [query.isLoading, query.isError, query.error, tickets]);

  return (
    <div className="p-8 space-y-6">
      <NoApiKeyBanner tenantCode={tenantCode} />
      <header className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-4xl">Support</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Tickets pushed in by your systems via the public API, plus anything
            opened here directly. The CS agent picks them up and replies.
          </p>
        </div>
        <Button asChild>
          <Link to="/support/new">
            <Plus className="h-4 w-4" />
            New ticket
          </Link>
        </Button>
      </header>

      <div className="flex items-center gap-2 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setFilter(f.value)}
            className={
              "rounded-full px-3 py-1 text-xs font-medium transition-colors " +
              (filter === f.value
                ? "bg-brand-violet text-white"
                : "border border-border text-muted-foreground hover:text-foreground")
            }
          >
            {f.label}
          </button>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">{body}</CardContent>
      </Card>
    </div>
  );
}

function TicketsTable({ tickets }: { tickets: Ticket[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-surface-2 text-[11px] uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="text-left px-5 py-3 font-medium">ID</th>
            <th className="text-left px-5 py-3 font-medium">Subject</th>
            <th className="text-left px-5 py-3 font-medium">Status</th>
            <th className="text-left px-5 py-3 font-medium">Priority</th>
            <th className="text-left px-5 py-3 font-medium">Updated</th>
          </tr>
        </thead>
        <tbody>
          {tickets.map((t) => (
            <tr key={t.id} className="border-t border-border hover:bg-muted/30">
              <td className="px-5 py-3">
                <Link to={`/support/${t.id}`} className="font-mono text-xs text-brand-cyan">
                  {t.external_id}
                </Link>
              </td>
              <td className="px-5 py-3">
                <Link to={`/support/${t.id}`} className="hover:underline">
                  {t.subject}
                </Link>
              </td>
              <td className="px-5 py-3">
                <StatusBadge status={t.status} />
              </td>
              <td className="px-5 py-3">
                <PriorityBadge priority={t.priority} />
              </td>
              <td className="px-5 py-3 text-xs text-muted-foreground">
                {t.updated_at ? formatDistanceToNow(new Date(t.updated_at), { addSuffix: true }) : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusBadge({ status }: { status: Ticket["status"] }) {
  const map: Record<Ticket["status"], { label: string; variant: React.ComponentProps<typeof Badge>["variant"] }> = {
    reported:         { label: "Reported",         variant: "running" },
    queued:           { label: "Queued",           variant: "secondary" },
    workinprogress:   { label: "In progress",      variant: "awaiting" },
    pending_approval: { label: "Pending approval", variant: "paused" },
    resolved:         { label: "Resolved",         variant: "ok" },
    notabug:          { label: "Not a bug",        variant: "muted" },
    closed:           { label: "Closed",           variant: "muted" },
  };
  const m = map[status] ?? { label: status, variant: "muted" as const };
  return <Badge variant={m.variant}>{m.label}</Badge>;
}

function PriorityBadge({ priority }: { priority: Ticket["priority"] }) {
  const map: Record<Ticket["priority"], { label: string; variant: React.ComponentProps<typeof Badge>["variant"] }> = {
    P1: { label: "P1 · Urgent", variant: "failed" },
    P2: { label: "P2 · High", variant: "awaiting" },
    P3: { label: "P3 · Medium", variant: "paused" },
    P4: { label: "P4 · Low", variant: "muted" },
  };
  const m = map[priority] ?? { label: priority, variant: "muted" as const };
  return <Badge variant={m.variant}>{m.label}</Badge>;
}

function EmptyOrError({
  Icon,
  title,
  body,
}: {
  Icon: React.ComponentType<{ className?: string }>;
  title: string;
  body: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center px-6">
      <Icon className="h-10 w-10 text-brand-ink/30 mb-3" />
      <p className="font-medium">{title}</p>
      <p className="mt-1 text-sm text-muted-foreground max-w-md">{body}</p>
    </div>
  );
}
